import { zodResolver } from "@hookform/resolvers/zod"
import { Link } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import { Card, CardDescription, CardTitle } from "design-system/molecules/Card"
import { InputColor } from "design-system/molecules/InputColor"
import { Section } from "design-system/molecules/Section"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { UploadS3Input } from "design-system/organisms/UploadS3Input"
import { getErrorTranslations } from "lib/i18n"
import { useGetIframeChatConfigQuery } from "modules/campaigns/hooks/sections/widget/useGetIframeChatConfigQuery"
import { useGetWidgetConfigurationQuery } from "modules/campaigns/hooks/sections/widget/useGetWidgetConfigurationQuery"
import { useUpdateIframeChatConfigMutation } from "modules/campaigns/hooks/sections/widget/useUpdateIframeChatConfigMutation"
import { useUploadIframeBackgroundImageMutation } from "modules/campaigns/hooks/sections/widget/useUploadIframeBackgroundImageMutation"
import {
  defaultIframeChatPhone,
  iframeChatPhoneOptions,
} from "modules/campaigns/lib/templates/widget/iframeChatPhoneOptions"
import { HeaderButtons } from "modules/shared/components/Breadcrumbs/HeaderButtons"
import { useMemo } from "react"
import { useForm } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import {
  GetWidgetConfigurationResponseData,
  IframeChatConfigResponseData,
  UpdateIframeChatConfigSchema,
} from "schema"
import { toast } from "sonner"
import type z from "zod"
import { WidgetConfigurationTabs } from "../WidgetConfigurationTabs"
import { IframeWidgetPreview } from "./IframeWidgetPreview"

export type WidgetPhoneMockupConfigurationFormType = z.infer<typeof UpdateIframeChatConfigSchema>

const defaultIframeConfig = IframeChatConfigResponseData.parse({})
const defaultWidgetConfig = GetWidgetConfigurationResponseData.parse({})
const backgroundPositionOptions = [
  { label: "Center", value: "center center" },
  { label: "Top", value: "center top" },
  { label: "Bottom", value: "center bottom" },
  { label: "Left", value: "left center" },
  { label: "Right", value: "right center" },
]
const backgroundSizeOptions = [
  { label: "Cover", value: "cover" },
  { label: "Contain", value: "contain" },
  { label: "Auto", value: "auto" },
]

export const WidgetPhoneMockup = () => {
  const { t } = useTranslation()
  const { mutateAsync, isPending: mutationIsPending } = useUpdateIframeChatConfigMutation()
  const {
    mutateAsync: uploadIframeBackgroundImage,
    isPending: isUploadIframeBackgroundImagePending,
  } = useUploadIframeBackgroundImageMutation()
  const form = useForm<WidgetPhoneMockupConfigurationFormType>({
    resolver: zodResolver(UpdateIframeChatConfigSchema),
    defaultValues: defaultIframeConfig,
    disabled: mutationIsPending,
  })

  const { isPending: iframeConfigIsPending } = useGetIframeChatConfigQuery({
    onSuccess: (data) => {
      form.reset(IframeChatConfigResponseData.parse(data))
    },
  })
  const { data: webChatConfig = defaultWidgetConfig, isPending: widgetConfigIsPending } =
    useGetWidgetConfigurationQuery({})
  const parsedWebChatConfig = useMemo(
    () => GetWidgetConfigurationResponseData.parse(webChatConfig),
    [webChatConfig],
  )

  const onSubmit = (payload: WidgetPhoneMockupConfigurationFormType) => {
    mutateAsync(payload).then(() => {
      toast.success(t("campaigns.templates.widget.iframe.saveSuccess"))
    })
  }

  const selectedPhone = form.watch("phone")
  const backgroundImage = form.watch("background.image")
  const selectedPhoneId =
    iframeChatPhoneOptions.find((phone) => phone.image === selectedPhone.image)?.id ??
    defaultIframeChatPhone.id

  const isPending = iframeConfigIsPending || widgetConfigIsPending

  return (
    <>
      <HeaderButtons identifier="saveWidgetPhoneMockupConfiguration">
        <Button disabled={!form.formState.isValid} onClick={form.handleSubmit(onSubmit)}>
          {t("campaigns.templates.openingMessages.form.fields.save")}
        </Button>
      </HeaderButtons>
      <div className="flex flex-col gap-4 h-full">
        <WidgetConfigurationTabs />
        <Section
          title={t("campaigns.templates.widget.iframe.title")}
          description={
            <Trans
              i18nKey="campaigns.templates.widget.iframe.description"
              components={{
                a: (
                  <Link
                    className="underline text-muted-foreground"
                    to="/settings/channels/widget"
                  />
                ),
              }}
            />
          }
        >
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            <Form
              {...form}
              errorTranslations={getErrorTranslations()}
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-4 w-full xl:col-span-6 lg:col-span-5 xl:min-w-0"
            >
              <Card className="flex flex-col gap-4 p-4">
                <div className="flex flex-col gap-1">
                  <CardTitle className="text-base">
                    {t("campaigns.templates.widget.iframe.phone.title")}
                  </CardTitle>
                  <CardDescription>
                    {t("campaigns.templates.widget.iframe.phone.description")}
                  </CardDescription>
                </div>
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("campaigns.templates.widget.iframe.phone.model")}</FormLabel>
                      <FormControl>
                        <Select
                          value={selectedPhoneId}
                          onValueChange={(value) => {
                            const nextPhone =
                              iframeChatPhoneOptions.find((phone) => phone.id === value) ??
                              defaultIframeChatPhone
                            form.setValue(
                              field.name,
                              {
                                image: nextPhone.image,
                                padding: nextPhone.padding,
                                iframePadding: nextPhone.iframePadding,
                                borderRadius: nextPhone.borderRadius,
                                aspectRatio: nextPhone.aspectRatio,
                              },
                              { shouldDirty: true, shouldValidate: true },
                            )
                          }}
                        >
                          <SelectTrigger className="max-w-lg">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {iframeChatPhoneOptions.map((phone) => (
                              <SelectItem key={phone.id} value={phone.id}>
                                {phone.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </Card>
              <Card className="flex flex-col gap-4 p-4">
                <div className="flex flex-col gap-1">
                  <CardTitle className="text-base">
                    {t("campaigns.templates.widget.iframe.background.title")}
                  </CardTitle>
                  <CardDescription>
                    {t("campaigns.templates.widget.iframe.background.description")}
                  </CardDescription>
                </div>
                <div className="flex flex-col gap-4">
                  <FormField
                    control={form.control}
                    name="background.color"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("campaigns.templates.widget.iframe.background.color")}
                        </FormLabel>
                        <FormControl>
                          <InputColor
                            {...field}
                            required
                            placeholder="#f5f5f5"
                            className="max-w-lg"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="background.image"
                    render={() => (
                      <FormItem>
                        <FormLabel>
                          {t("campaigns.templates.widget.iframe.background.image")}
                        </FormLabel>
                        <FormControl>
                          {backgroundImage ? (
                            <div className="flex flex-col gap-3">
                              <img
                                src={backgroundImage}
                                alt=""
                                className="w-full max-w-lg aspect-video rounded-md object-cover border border-border"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                type="button"
                                className="w-fit"
                                onClick={() =>
                                  form.setValue("background.image", "", {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  })
                                }
                              >
                                {t("campaigns.templates.widget.iframe.background.removeImage")}
                              </Button>
                            </div>
                          ) : (
                            <UploadS3Input
                              onChange={(media) => {
                                form.setValue("background.image", media.url || "", {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                })
                              }}
                              placeholder={t(
                                "campaigns.templates.widget.iframe.background.imagePlaceholder",
                              )}
                              mutateAsync={uploadIframeBackgroundImage}
                              isPending={isUploadIframeBackgroundImagePending}
                              disabled={isUploadIframeBackgroundImagePending}
                            />
                          )}
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {backgroundImage && (
                    <div className="grid grid-cols-2 gap-3">
                      <FormField
                        control={form.control}
                        name="background.position"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("campaigns.templates.widget.iframe.background.position")}
                            </FormLabel>
                            <FormControl>
                              <Select
                                value={field.value}
                                onValueChange={(value) =>
                                  form.setValue(field.name, value, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  })
                                }
                              >
                                <SelectTrigger className="max-w-lg">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {backgroundPositionOptions.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="background.size"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("campaigns.templates.widget.iframe.background.size")}
                            </FormLabel>
                            <FormControl>
                              <Select
                                value={field.value}
                                onValueChange={(value) =>
                                  form.setValue(field.name, value, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  })
                                }
                              >
                                <SelectTrigger className="max-w-lg">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {backgroundSizeOptions.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
                </div>
              </Card>
            </Form>
            <div className="flex flex-col gap-4 h-full w-full lg:col-span-7 xl:col-span-6 xl:min-w-0">
              <IframeWidgetPreview
                form={form}
                webChatConfig={parsedWebChatConfig}
                isLoading={isPending}
              />
            </div>
          </div>
        </Section>
      </div>
    </>
  )
}
